export const TILE_SIZE = 32;

export const enum Tile {
  Wall = 0,
  Floor = 1,
  Base = 2,
  Door = 3,
}

export type DoorDirection = 'north' | 'south' | 'west' | 'east';

export interface DoorPosition {
  direction: DoorDirection;
  x: number;
  y: number;
}

/**
 * Represents the labyrinth map. Can be constructed from loaded SVG data.
 */
export class Labyrinth {
  width: number;
  height: number;
  tiles: Tile[][];
  baseTile: { x: number; y: number };
  doors: DoorPosition[];

  /** World-coordinate tile keys ("x,y") blocked by placed buildings. */
  private _buildingBlocked = new Set<string>();

  /** Cached count of non-wall tiles (computed once at construction). */
  readonly walkableCount: number;

  constructor(
    width: number,
    height: number,
    tiles: Tile[][],
    baseTile: { x: number; y: number },
    doors: DoorPosition[] = [],
  ) {
    this.width = width;
    this.height = height;
    this.tiles = tiles;
    this.baseTile = baseTile;
    this.doors = doors;

    let count = 0;
    for (let y = 0; y < height; y++)
      for (let x = 0; x < width; x++)
        if (tiles[y]![x] !== Tile.Wall) count++;
    this.walkableCount = count;
  }

  isWalkable(x: number, y: number): boolean {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) return false;
    if (this._buildingBlocked.has(`${x},${y}`)) return false;
    return this.tiles[y]![x] !== Tile.Wall;
  }

  /** Replace the set of tiles blocked by buildings (world coordinates). */
  setBuildingBlocked(tiles: Set<string>): void {
    this._buildingBlocked = tiles;
  }

  getDoor(direction: DoorDirection): DoorPosition | undefined {
    return this.doors.find(d => d.direction === direction);
  }
}
